import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (mounted) setUser(r.data.user);
      } catch (e) {
        // No valid session cookie is the expected path for anonymous users —
        // logged at debug so it doesn't spam prod but is available when
        // diagnosing "why am I stuck on the login screen" issues.
        if (e?.response?.status && e.response.status !== 401) {
          console.warn("[auth] /auth/me failed unexpectedly:", e.response.status);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Best-effort — the server-side session may already be dead. Still
      // clear the client state and bounce home so the user isn't stuck.
      console.warn("[auth] logout request failed, clearing client state anyway:", e?.message);
    }
    setUser(null);
    window.location.href = "/";
  }, []);

  const loginWithUser = useCallback((userObj) => {
    setUser(userObj);
  }, []);

  const value = useMemo(
    () => ({ user, loading, logout, loginWithUser }),
    [user, loading, logout, loginWithUser]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
